import { useState } from "react";
import { Save, Loader2, Eye, EyeOff, Star, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useContent } from "@/contexts/ContentContext";
import ImageUploader from "@/components/admin/ImageUploader";
import BannerEditor from "@/components/admin/BannerEditor";

export const HeroEditor = () => {
  const { content, update } = useContent();
  const [draft, setDraft] = useState(content.hero);
  const [busy, setBusy] = useState(false);

  const onField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await update("hero", draft);
      toast.success("Hero updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="hero-editor">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hero-line1">Headline (line 1)</Label>
              <Input id="hero-line1" data-testid="hero-editor-line1" value={draft.headline_line1 || ""} onChange={onField("headline_line1")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hero-line2">Headline (line 2, accent)</Label>
              <Input id="hero-line2" data-testid="hero-editor-line2" value={draft.headline_line2 || ""} onChange={onField("headline_line2")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hero-badge">Badge label</Label>
            <Input id="hero-badge" data-testid="hero-editor-badge" value={draft.badge_label || ""} onChange={onField("badge_label")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hero-sub">Subheadline</Label>
            <Textarea id="hero-sub" data-testid="hero-editor-sub" rows={3} value={draft.subheadline || ""} onChange={onField("subheadline")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hero-cta1">Primary CTA label</Label>
              <Input id="hero-cta1" data-testid="hero-editor-cta1" value={draft.primary_cta_label || ""} onChange={onField("primary_cta_label")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hero-cta2">Secondary CTA label</Label>
              <Input id="hero-cta2" data-testid="hero-editor-cta2" value={draft.secondary_cta_label || ""} onChange={onField("secondary_cta_label")} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <ImageUploader
            value={draft.phone_screen_image_id}
            onChange={(id) => setDraft({ ...draft, phone_screen_image_id: id })}
            label="Hero phone screen image"
            testId="hero-editor-phone-image"
            aspect="9/19.5"
          />
          <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
            Optional. Upload a portrait app screenshot (recommended 9:19.5 ratio) to replace the built-in Clever Canine preview inside the hero phone. Clear it to bring the default art back.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Label className="text-base">Trust row (below the CTAs)</Label>
            <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
              The compact row that shows rating, tagline, and the &ldquo;NEW&rdquo; version pill. Toggle each item on/off; clear text to hide individual labels.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {/* Rating */}
          <div
            data-testid="hero-editor-rating-row"
            className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 sm:p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {draft.rating_visible !== false ? (
                  <Eye className="h-4 w-4 text-[#1F9D6A]" />
                ) : (
                  <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                )}
                <Star className="h-4 w-4 text-[#F35A1F]" />
                <span className="text-sm font-semibold">Average rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="hero-rating-visible" className="text-xs text-[hsl(var(--muted-foreground))]">
                  {draft.rating_visible !== false ? "On" : "Off"}
                </Label>
                <Switch
                  id="hero-rating-visible"
                  data-testid="hero-editor-rating-visible"
                  checked={draft.rating_visible !== false}
                  onCheckedChange={(v) => setDraft({ ...draft, rating_visible: v })}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Value</Label>
                <Input
                  data-testid="hero-editor-rating-value"
                  value={draft.rating_value || ""}
                  onChange={onField("rating_value")}
                  placeholder="4.8"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-xs">Label</Label>
                <Input
                  data-testid="hero-editor-rating-label"
                  value={draft.rating_label || ""}
                  onChange={onField("rating_label")}
                  placeholder="avg rating"
                />
              </div>
            </div>
          </div>

          {/* Tagline (middle) */}
          <div
            data-testid="hero-editor-tagline-row"
            className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 sm:p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {draft.tagline_visible !== false ? (
                  <Eye className="h-4 w-4 text-[#1F9D6A]" />
                ) : (
                  <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                )}
                <Heart className="h-4 w-4 text-[#F35A1F]" />
                <span className="text-sm font-semibold">Tagline</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="hero-tagline-visible" className="text-xs text-[hsl(var(--muted-foreground))]">
                  {draft.tagline_visible !== false ? "On" : "Off"}
                </Label>
                <Switch
                  id="hero-tagline-visible"
                  data-testid="hero-editor-tagline-visible"
                  checked={draft.tagline_visible !== false}
                  onCheckedChange={(v) => setDraft({ ...draft, tagline_visible: v })}
                />
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Label className="text-xs">Text</Label>
              <Input
                data-testid="hero-editor-tagline-text"
                value={draft.tagline_text || ""}
                onChange={onField("tagline_text")}
                placeholder="Built for everyday joy"
              />
            </div>
          </div>

          {/* NEW badge */}
          <div
            data-testid="hero-editor-newbadge-row"
            className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 sm:p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {draft.new_badge_visible !== false ? (
                  <Eye className="h-4 w-4 text-[#1F9D6A]" />
                ) : (
                  <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                )}
                <Sparkles className="h-4 w-4 text-[#F35A1F]" />
                <span className="text-sm font-semibold">“NEW” badge</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="hero-newbadge-visible" className="text-xs text-[hsl(var(--muted-foreground))]">
                  {draft.new_badge_visible !== false ? "On" : "Off"}
                </Label>
                <Switch
                  id="hero-newbadge-visible"
                  data-testid="hero-editor-newbadge-visible"
                  checked={draft.new_badge_visible !== false}
                  onCheckedChange={(v) => setDraft({ ...draft, new_badge_visible: v })}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Badge label</Label>
                <Input
                  data-testid="hero-editor-newbadge-label"
                  value={draft.new_badge_label || ""}
                  onChange={onField("new_badge_label")}
                  placeholder="NEW"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-xs">Adjacent text</Label>
                <Input
                  data-testid="hero-editor-newbadge-text"
                  value={draft.new_badge_text || ""}
                  onChange={onField("new_badge_text")}
                  placeholder="Clever Canine v1.2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--accent))]/40 p-4 sm:p-5">
        <BannerEditor
          banners={draft.floating_banners}
          onChange={(next) => setDraft({ ...draft, floating_banners: next })}
          testIdPrefix="hero-banner"
          helperText="Decorative tiles that float around the hero phone (hidden on mobile)."
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="hero-editor-save" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Hero
        </Button>
      </div>
    </div>
  );
};

export default HeroEditor;
